import type { MeScopes, OutletSummary } from "@kopi/contracts";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { meScopesQuery, outletsQuery } from "@/lib/queries";

const STORAGE_KEY = "kopi-outlet";

/** The outlet this device last chose (the switcher's choice), if any; still to be checked in scope. */
export function readStored(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStored(outletId: string) {
  try {
    window.localStorage.setItem(STORAGE_KEY, outletId);
  } catch {
    // Blocked storage: the choice lasts for this visit only.
  }
}

interface CurrentOutletState {
  /** The outlet the shell is showing; undefined when the tenant has none. */
  outlet: OutletSummary | undefined;
  /** Outlets the user can switch to (a small, bounded set: CODING_STANDARDS §4 exception). */
  outlets: OutletSummary[];
  /** False on a register: its session is bound to the register's outlet. */
  switchable: boolean;
  /** A register session: the outlet comes from the device, not the stored choice. */
  deviceBound: boolean;
  setOutletId: (outletId: string) => void;
}

const CurrentOutletContext = createContext<CurrentOutletState | null>(null);

export function useCurrentOutlet(): CurrentOutletState {
  const state = useContext(CurrentOutletContext);
  if (!state) throw new Error("useCurrentOutlet must be used inside <CurrentOutletProvider>");
  return state;
}

/**
 * The outlet a screen shows: a register's own outlet; else the device's remembered choice while it is in
 * scope, else the first active outlet, else the first. The one rule, for the shell and for route loaders
 * that start an outlet's requests before the shell renders.
 */
export function pickOutlet(
  scopes: Pick<MeScopes, "device">,
  outlets: readonly OutletSummary[],
  chosen: string | null,
): OutletSummary | undefined {
  const pick = (id: string | null | undefined) => (id ? outlets.find((o) => o.id === id) : undefined);
  const deviceOutlet = scopes.device?.outletId;
  if (deviceOutlet) return pick(deviceOutlet);
  return pick(chosen) ?? outlets.find((o) => o.active) ?? outlets[0];
}

/**
 * The outlet switcher's choice (sidebar foot), remembered per device. Screens use it as their default
 * outlet (eyebrow, devices, prices); a register always shows its own outlet.
 */
export function CurrentOutletProvider(props: { children: ReactNode }) {
  const { data: scopes } = useSuspenseQuery(meScopesQuery);
  const { data: outlets } = useSuspenseQuery(outletsQuery);
  const [chosen, setChosen] = useState<string | null>(readStored);

  const value = useMemo<CurrentOutletState>(() => {
    const deviceOutlet = scopes.device?.outletId;
    const outlet = pickOutlet(scopes, outlets, chosen);
    return {
      outlet,
      outlets,
      deviceBound: Boolean(deviceOutlet),
      switchable: !deviceOutlet && outlets.length > 1,
      setOutletId: (outletId) => {
        writeStored(outletId);
        setChosen(outletId);
      },
    };
  }, [scopes.device?.outletId, outlets, chosen]);

  // A remembered outlet that left the user's scope (deactivated access, another tenant on this device)
  // is replaced by the fallback, and the fallback is remembered so the next visit starts there.
  const replacement = value.deviceBound ? undefined : value.outlet?.id;
  useEffect(() => {
    if (chosen && replacement && replacement !== chosen) {
      writeStored(replacement);
      setChosen(replacement);
    }
  }, [replacement, chosen]);

  return <CurrentOutletContext.Provider value={value}>{props.children}</CurrentOutletContext.Provider>;
}
